import { useRef } from 'react'
import React from 'react'
import Tags from 'components/common/TagsGray/TagsGray'
import styles from './TopCaseStudy.module.scss'
import Button from 'components/common/Button/Button'
import animationStyles from 'components/common/Animation/Animation.module.scss'
import useElementVisible from 'components/common/Animation/useElementVisible'

const caseStudies: {
  rel: string
  addstyle: string
  visible: string
  title: string | React.ReactNode
  company: string
  imageSrc: string
  link: string
  tag1text: string
  tag1src: string
  tag2text?: string
  tag2src?: string
  tag3text?: string
  tag3src?: string
  tag4text?: string
  tag4src?: string
  industryTagText: string
  industryTagSrc: string
}[] = [
  {
    rel: 'rel1',
    addstyle: 'topCS__one',
    visible: 'isVisible1',
    title: '課題解決率が大幅向上 - “使える生成AIチャット”をアジャイル×UX/UIでスピード実現',
    company: '株式会社大和総研',
    imageSrc: '/img/cases/cases_dir.webp',
    link: '/cases/dir/',
    tag1text: 'アプリケーション開発',
    tag1src: '/cases/application-development/',
    tag2text: 'フロントエンド開発',
    tag2src: '/cases/frontend-dev/',
    tag3text: 'UX/UI改善',
    tag3src: '/cases/uxui-improvement/',
    tag4text: 'AI',
    tag4src: '/cases/ai/',
    industryTagText: 'IT/情報/通信',
    industryTagSrc: '/cases/telecommunications/',
  },
  {
    rel: 'rel2',
    addstyle: 'topCS__LeftLarge',
    visible: 'isVisible2',
    title: <>全3回のUX/UIワークショップで、プロジェクトを前に進める共通言語を獲得</>,
    company: '株式会社JTBビジネストラベルソリューションズ',
    imageSrc: '/img/cases/cases_jtbcwt.webp',
    link: '/cases/jtb-cwt/',
    tag1text: 'UX/UI改善',
    tag1src: '/cases/uxui-improvement/',
    industryTagText: 'サービス',
    industryTagSrc: '/cases/service/',
  },
  {
    rel: 'rel3',
    addstyle: 'topCS__RightSmall',
    visible: 'isVisible3',
    title: <>AI時代の開発生産性を支えるデザインシステムを構築</>,
    company: '株式会社アルダグラム',
    imageSrc: '/img/cases/cases_aldagram.webp',
    link: '/cases/aldagram/',
    tag1text: 'デザインシステム構築支援',
    tag1src: '/cases/design-system/',
    industryTagText: 'IT/情報/通信',
    industryTagSrc: '/cases/telecommunications/',
  },
  {
    rel: 'rel4',
    addstyle: 'topCS__LeftSmall',
    visible: 'isVisible4',
    title: <>海外送金アプリを刷新し、ロイヤルティ向上と改修工数削減を実現</>,
    company: 'SBIレミット株式会社',
    imageSrc: '/img/cases/cases_remit.webp',
    link: '/cases/remit/',
    tag1text: 'アプリケーション開発',
    tag1src: '/cases/application-development/',
    tag2text: 'フロントエンド開発',
    tag2src: '/cases/frontend-dev/',
    tag3text: 'UX/UI改善',
    tag3src: '/cases/uxui-improvement/',
    industryTagText: '金融/不動産',
    industryTagSrc: '/cases/finance/',
  },
  {
    rel: 'rel5',
    addstyle: 'topCS__RightLarge',
    visible: 'isVisible5',
    title: (
      <>
        アジャイル開発により
        <br />
        Webサービスのアプリ化を2か月半で実現
      </>
    ),
    company: '株式会社SANU',
    imageSrc: '/img/cases/cases_sanu.webp',
    link: '/cases/sanu/',
    tag1text: 'アプリケーション開発',
    tag1src: '/cases/application-development/',
    tag2text: 'フロントエンド開発',
    tag2src: '/cases/frontend-dev/',
    industryTagText: 'サービス',
    industryTagSrc: '/cases/service/',
  },
  {
    rel: 'rel6',
    addstyle: 'topCS__LeftLarge',
    visible: 'isVisible6',
    title: '利用者10％増 - UX/UI改善で転職成功に必須のアプリを開発',
    company: '株式会社キャリアデザインセンター',
    imageSrc: '/img/cases/cases_cdc.webp',
    link: '/cases/career-design-center/',
    tag1text: 'アプリケーション開発',
    tag1src: '/cases/application-development/',
    tag2text: 'UX/UI改善',
    tag2src: '/cases/uxui-improvement/',
    tag3text: '内製化支援',
    tag3src: '/cases/inhouse-support/',
    industryTagText: 'サービス',
    industryTagSrc: '/cases/service/',
  },
  {
    rel: 'rel7',
    addstyle: 'topCS__RightSmall',
    visible: 'isVisible7',
    title: (
      <>
        不動産売買の契約業務をDX化
        <br />- UX/UI刷新でミスを削減し、作業時間を大幅に短縮
      </>
    ),
    company: '株式会社不動産SHOP ナカジツ',
    imageSrc: '/img/cases/cases_nakajitsu.webp',
    link: '/cases/nakajitsu/',
    tag1text: 'アプリケーション開発',
    tag1src: '/cases/application-development/',
    tag2text: 'フロントエンド開発',
    tag2src: '/cases/frontend-dev/',
    tag3text: 'UX/UI改善',
    tag3src: '/cases/uxui-improvement/',
    tag4text: '内製化支援',
    tag4src: '/cases/inhouse-support/',
    industryTagText: '金融/不動産',
    industryTagSrc: '/cases/finance/',
  },
]

const TopCaseStudy: React.FC = () => {
  const refs = caseStudies.map(() => useRef<HTMLDivElement>(null))
  const visibilityStatuses = refs.map((ref, index) => useElementVisible(ref, 0.1))

  const getTitleText = (title: string | React.ReactNode): string => {
    if (typeof title === 'string') {
      return title
    }
    // JSXから文字列を抽出
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === 'string' || typeof node === 'number') {
        return String(node)
      }
      if (Array.isArray(node)) {
        return node.map(extractText).join('')
      }
      if (React.isValidElement(node) && node.props.children) {
        return extractText(node.props.children)
      }
      return ''
    }
    return extractText(title)
  }

  return (
    <section className={styles.topCaseStudy}>
      {caseStudies.map((study, index) => (
        <article
          key={study.rel}
          ref={refs[index]}
          className={`${styles.topCS} ${styles[study.addstyle]} ${animationStyles.fade} ${
            visibilityStatuses[index] ? animationStyles.fadeOn : ''
          }`}
        >
          <a href={study.link} className={styles.topCS__link}>
            <figure className={styles.topCS__link__image}>
              <img src={study.imageSrc} alt={getTitleText(study.title)} width={578} height={393} />
            </figure>
            <div className={styles.topCS__link__title}>{study.title}</div>
            <div className={styles.topCS__link__company}>{study.company}</div>
          </a>
          <div className={styles.topCS__link__tag}>
            <Tags
              tag1src={study.tag1src}
              tag1text={study.tag1text}
              tag2src={study.tag2src || undefined}
              tag2text={study.tag2text || undefined}
              tag3src={study.tag3src || undefined}
              tag3text={study.tag3text || undefined}
              tag4src={study.tag4src || undefined}
              tag4text={study.tag4text || undefined}
              industryTagText={study.industryTagText}
              industryTagSrc={study.industryTagSrc}
            />
          </div>
        </article>
      ))}
      <div className={styles.topCS__viewmore}>
        <Button text="事例一覧を見る" href="/cases/" />
      </div>
    </section>
  )
}

export default TopCaseStudy
